import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { EquipmentTimersDurationByEquipmentListChart, takeRankedChartSeries } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { EquipmentExtendedClient } from '@frontend/equipment/api';
import { EquipmentExtended } from '@frontend/equipment/types';
import { useEffect, useState } from 'react';

import { getLast30DaysChartRange } from '../../../statistics/pudo/last-30-days-chart-range';
import { useInsightFetchRange } from '../insight-detail/use-insight-fetch-range';
import { LendingEquipmentUsageDisplay, toLendingEquipmentUsageDisplay } from './format-lending-equipment-usage-display-label';

const EQUIPMENT_LIMIT = 8;

export interface EquipmentUsageHoursItem extends LendingEquipmentUsageDisplay {
    key: string;
    /** Completed equipment-timer duration in seconds. */
    durationSeconds: number;
}

const toUsageItems = (chart: Chart): EquipmentUsageHoursItem[] =>
    chart.series.flatMap((series) => {
        const rawName = series.name?.trim();
        if (!rawName || rawName.toLowerCase() === 'default') {
            return [];
        }

        const durationSeconds = series.data.reduce((sum, point) => sum + point.value, 0);
        if (durationSeconds <= 0) {
            return [];
        }

        return [
            {
                key: rawName,
                durationSeconds,
                ...toLendingEquipmentUsageDisplay(null, rawName)
            }
        ];
    });

const resolveEquipmentLabels = async (accountId: string, items: EquipmentUsageHoursItem[]): Promise<EquipmentUsageHoursItem[]> => {
    const equipmentById = new Map<string, EquipmentExtended>();

    await Promise.all(
        items.map(async (item) => {
            try {
                const equipment = await EquipmentExtendedClient.fetchEquipmentExtended(accountId, item.key);
                equipmentById.set(item.key, equipment);
            } catch {
                // Keep fallback display from chart series key.
            }
        })
    );

    return items.map((item) => ({
        ...item,
        ...toLendingEquipmentUsageDisplay(equipmentById.get(item.key), item.key)
    }));
};

export const useLendingEquipmentUsageHoursList = (accountId: string) => {
    const [items, setItems] = useState<EquipmentUsageHoursItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        ChartClient.fetchChartData(accountId, EquipmentTimersDurationByEquipmentListChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams,
            [DefaultQueryParams.SORTDIRECTION]: 'desc'
        })
            .then(async (chart) => {
                if (cancelled) return;
                const highest = takeRankedChartSeries(chart, 'highest', EQUIPMENT_LIMIT);
                const labeledItems = await resolveEquipmentLabels(accountId, toUsageItems(highest));
                if (!cancelled) {
                    setItems(labeledItems);
                }
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, fetchRange.key]);

    return { items, isLoading, hasData: items.length > 0 };
};
